import { useQuery } from '@tanstack/react-query';
import { useOrgId } from './useOrgId';
import { resolveShareOrigin } from '@/lib/shareUrl';

/**
 * useShareOrigin — the host every client-facing link in the studio's
 * workspace should be built on.
 *
 * Returns the studio's live custom domain (`https://studio.com`) when
 * they have one, and this app's own origin otherwise. No trailing
 * slash, so callers concatenate a leading-slash path onto it.
 *
 * WHY A HOOK AND NOT `buildShareUrl`
 *
 * `buildShareUrl` is async, which suits the handful of call sites that
 * were already awaiting something (Galleries, Deliveries). Most links
 * aren't like that: a signing URL, a document link or a booking URL is
 * computed during render, or read synchronously inside a click handler
 * that then writes to the clipboard. Awaiting inside that handler is
 * worse than it looks — Safari only honours `navigator.clipboard.write`
 * while the user gesture is still on the stack, so a lookup in the
 * middle of the handler can lose the copy entirely.
 *
 * So the lookup happens once, on mount, and render stays synchronous.
 *
 * The resolved value is cached per-org by both react-query and the
 * module-level cache inside shareUrl, so this is one round-trip per
 * session, not per component. Until it lands the hook returns the
 * app's own origin — the same answer every one of these call sites
 * gave unconditionally before, so the worst case is the old behaviour
 * for the first moment after mount, and it self-corrects.
 */
export function useShareOrigin(): string {
  const { data: orgId } = useOrgId();

  const { data } = useQuery({
    queryKey: ['share-origin', orgId ?? null],
    queryFn: () => resolveShareOrigin(orgId),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  return data ?? window.location.origin;
}
